import React, { useEffect, useState } from "react";
import { IResourceComponentsProps } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { Column, ColumnDef, flexRender } from "@tanstack/react-table";
import {
  ScrollArea,
  Table,
  Pagination,
  Flex,
  Select,
  LoadingOverlay,
  Text,
  Box,
  TextInput,
  TextInputProps,
  Paper,
  Group,
  ActionIcon,
  Collapse,
  Badge,
  UnstyledButton,
  Menu,
  Grid,
  ThemeIcon,
  Tooltip,
  Skeleton,
  Modal,
  Button,
} from "@mantine/core";
import { DatePicker, TimeInput } from "@mantine/dates";
import { useApiUrl, useCustomMutation } from "@refinedev/core";
import { openConfirmModal } from "@mantine/modals";
import { showNotification } from "@mantine/notifications";
import { List, DateField } from "@refinedev/mantine";
import {
  IconFilter,
  IconFilterOff,
  IconChevronUp,
  IconChevronDown,
  IconX,
  IconDots,
  IconEye,
  IconSearch,
  IconClock,
  IconCalendarEvent,
  IconSend,
  IconAlertCircle,
  IconCircleCheck,
  IconMail,
  IconCalendarTime,
} from "@tabler/icons";
import { useNavigate } from "react-router-dom";
import {
  ReviewRequest,
  SOURCE_OPTIONS,
  SOURCE_COLORS,
  SOURCE_LABELS,
  STATUS_OPTIONS,
  STATUS_LABELS,
} from "entities/review_request";
import { DateFilter } from "components/common/filters/DateFilter";
import { countryCodeToFlag } from "utils/countryFlag";

/* ── Debounced filter input ─────────────────────────────────── */
function DebouncedColumnInput({
  column,
  delay = 500,
  ...inputProps
}: {
  column: Column<ReviewRequest, unknown>;
  delay?: number;
} & Omit<TextInputProps, "value" | "onChange">) {
  const columnValue = (column.getFilterValue() as string) ?? "";
  const [localValue, setLocalValue] = useState(columnValue);

  useEffect(() => {
    setLocalValue(columnValue);
  }, [columnValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      column.setFilterValue(localValue || undefined);
    }, delay);
    return () => clearTimeout(timeout);
  }, [localValue, delay, column]);

  return (
    <TextInput
      value={localValue}
      onChange={(e) => setLocalValue(e.currentTarget.value)}
      icon={<IconSearch size={14} />}
      {...inputProps}
    />
  );
}

/* ── Status indicator (dot + label) ─────────────────────────── */
function StatusIndicator({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    scheduled: "#339af0",
    sent: "#40c057",
    failed: "#fa5252",
  };
  const color = colorMap[status] || "#868e96";

  return (
    <Group spacing={6} noWrap>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: color,
          flexShrink: 0,
          boxShadow: `0 0 6px ${color}44`,
        }}
      />
      <Text
        size="xs"
        weight={600}
        transform="uppercase"
        sx={{ letterSpacing: 0.5 }}
      >
        {STATUS_LABELS[status] || status}
      </Text>
    </Group>
  );
}

/* ── Source badge (subtle variant) ──────────────────────────── */
function SourceBadge({ source }: { source: string }) {
  return (
    <Badge
      variant="light"
      color={SOURCE_COLORS[source] || "gray"}
      size="sm"
      radius="xl"
      sx={{ fontWeight: 600 }}
    >
      {SOURCE_LABELS[source] || source}
    </Badge>
  );
}

/* ── Row action menu ────────────────────────────────────────── */
function RowActions({
  id,
  email,
  onSendEmail,
  onReschedule,
}: {
  id: string | number;
  email: string;
  onSendEmail: (id: string | number, email: string) => void;
  onReschedule: (id: string | number) => void;
}) {
  const navigate = useNavigate();

  return (
    <Menu shadow="lg" width={180} position="bottom" withinPortal radius="md">
      <Menu.Target>
        <ActionIcon variant="light" color="gray" size="sm" radius="xl">
          <IconDots size={14} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          icon={<IconEye size={14} />}
          onClick={() => navigate(`/review_requests/show/${id}`)}
        >
          View details
        </Menu.Item>
        <Menu.Item
          icon={<IconSend size={14} />}
          onClick={() => onSendEmail(id, email)}
        >
          Send email
        </Menu.Item>
        <Menu.Item
          icon={<IconCalendarTime size={14} />}
          onClick={() => onReschedule(id)}
        >
          Reschedule
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/* ── Stat card for summary row ──────────────────────────────── */
function StatCard({
  icon,
  label,
  value,
  color,
  isLoading,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
  isLoading: boolean;
}) {
  return (
    <Paper
      p="sm"
      radius="md"
      sx={(theme) => ({
        backgroundColor:
          theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.white,
        border: `1px solid ${
          theme.colorScheme === "dark"
            ? theme.colors.dark[4]
            : theme.colors.gray[2]
        }`,
        flex: "1 1 0",
        minWidth: 140,
      })}
    >
      <Group spacing="sm" noWrap>
        <ThemeIcon size={36} radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <Box>
          {isLoading ? (
            <Skeleton height={20} width={30} radius="sm" />
          ) : (
            <Text size="lg" weight={700} sx={{ lineHeight: 1.1 }}>
              {value}
            </Text>
          )}
          <Text size="xs" color="dimmed" weight={500} mt={2}>
            {label}
          </Text>
        </Box>
      </Group>
    </Paper>
  );
}

/* ── Main list component ────────────────────────────────────── */
export const ReviewRequestList: React.FC<IResourceComponentsProps> = () => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const apiUrl = useApiUrl();
  const { mutate: sendEmail } = useCustomMutation();
  const { mutate: reschedule, isLoading: isRescheduling } = useCustomMutation();
  const [rescheduleOpened, setRescheduleOpened] = useState(false);
  const [rescheduleId, setRescheduleId] = useState<string | number | null>(
    null,
  );
  const [rescheduleDate, setRescheduleDate] = useState<Date | null>(null);
  const [rescheduleTime, setRescheduleTime] = useState<Date | null>(null);

  const handleOpenReschedule = React.useCallback((id: string | number) => {
    setRescheduleId(id);
    setRescheduleDate(null);
    setRescheduleTime(null);
    setRescheduleOpened(true);
  }, []);

  const handleReschedule = () => {
    if (!rescheduleId || !rescheduleDate) return;
    const date = new Date(rescheduleDate);
    if (rescheduleTime) {
      date.setHours(
        rescheduleTime.getHours(),
        rescheduleTime.getMinutes(),
        0,
        0,
      );
    }
    reschedule(
      {
        url: `${apiUrl}/review_requests/${rescheduleId}/reschedule`,
        method: "patch",
        values: { scheduled_for: date.toISOString() },
      },
      {
        onSuccess: () => {
          showNotification({
            title: "Rescheduled",
            message: "Review request has been rescheduled successfully",
            color: "green",
          });
          setRescheduleOpened(false);
        },
        onError: (error: any) => {
          showNotification({
            title: "Failed to Reschedule",
            message:
              error?.response?.data?.error ||
              error?.message ||
              "An error occurred while rescheduling",
            color: "red",
          });
        },
      },
    );
  };

  const handleSendEmail = React.useCallback((id: string | number, email: string) => {
    openConfirmModal({
      title: "Send Review Request Email",
      children: (
        <Text size="sm">
          Are you sure you want to send the review request email to{" "}
          <strong>{email}</strong> immediately?
        </Text>
      ),
      labels: { confirm: "Send Email", cancel: "Cancel" },
      confirmProps: { color: "pink" },
      onConfirm: () => {
        sendEmail(
          {
            url: `${apiUrl}/review_requests/${id}/send_email`,
            method: "post",
            values: {},
          },
          {
            onSuccess: () => {
              showNotification({
                title: "Email Sent",
                message: `Review request email has been sent to ${email}`,
                color: "green",
              });
            },
            onError: (error: any) => {
              showNotification({
                title: "Failed to Send Email",
                message:
                  error?.response?.data?.error ||
                  error?.message ||
                  "An error occurred while sending the email",
                color: "red",
              });
            },
          },
        );
      },
    });
  }, [apiUrl, sendEmail]);

  const columns = React.useMemo<ColumnDef<ReviewRequest>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
        size: 70,
        enableColumnFilter: false,
        cell: ({ getValue }) => (
          <Text
            size="xs"
            color="dimmed"
            weight={600}
            sx={{ fontFamily: "monospace" }}
          >
            #{getValue<number>()}
          </Text>
        ),
      },
      {
        id: "email",
        accessorKey: "email",
        header: "Email",
        cell: function render({ getValue, row }) {
          const name = row.original.customer_name;
          return (
            <Box>
              {name && (
                <Text size="sm" weight={600} sx={{ lineHeight: 1.3 }}>
                  {name}
                </Text>
              )}
              <Text size="xs" color="dimmed" weight={500}>
                {getValue<string>()}
              </Text>
            </Box>
          );
        },
        meta: { filterOperator: "contains" },
      },
      {
        id: "source",
        accessorKey: "source",
        header: "Source",
        cell: ({ getValue }) => <SourceBadge source={getValue<string>()} />,
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => <StatusIndicator status={getValue<string>()} />,
      },
      {
        id: "order_number",
        accessorKey: "order_number",
        header: "Order",
        cell: function render({ getValue }) {
          const value = getValue<string>();
          if (!value)
            return (
              <Text size="xs" color="dimmed">
                —
              </Text>
            );
          return (
            <Text size="xs" weight={600} sx={{ fontFamily: "monospace" }}>
              {value}
            </Text>
          );
        },
        meta: { filterOperator: "contains" },
      },
      {
        id: "shipping_country",
        accessorKey: "shipping_country",
        header: "Country",
        size: 80,
        cell: function render({ getValue }) {
          const value = getValue<string>();
          if (!value)
            return (
              <Text size="xs" color="dimmed">
                —
              </Text>
            );
          return (
            <Group spacing={6} noWrap>
              <Text size="md" sx={{ lineHeight: 1 }}>
                {countryCodeToFlag(value)}
              </Text>
              <Text size="xs" weight={600} color="dimmed">
                {value}
              </Text>
            </Group>
          );
        },
        meta: { filterOperator: "contains" },
      },
      {
        id: "reviewed",
        accessorKey: "reviewed",
        header: "Reviewed",
        enableColumnFilter: false,
        size: 90,
        cell: ({ getValue }) => {
          const reviewed = getValue<boolean>();
          return reviewed ? (
            <ThemeIcon size={20} radius="xl" color="green" variant="light">
              <IconCircleCheck size={14} />
            </ThemeIcon>
          ) : (
            <ThemeIcon size={20} radius="xl" color="gray" variant="light">
              <IconClock size={14} />
            </ThemeIcon>
          );
        },
      },
      {
        id: "sent_at",
        accessorKey: "sent_at",
        header: "Sent At",
        cell: function render({ getValue }) {
          const value = getValue<string>();
          if (!value)
            return (
              <Text size="xs" color="dimmed">
                —
              </Text>
            );
          return (
            <Text size="xs" color="dimmed">
              <DateField value={value} format="MMM D, YYYY HH:mm" />
            </Text>
          );
        },
        meta: { filterOperator: "between" },
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: function render({ getValue }) {
          return (
            <Text size="xs" color="dimmed">
              <DateField value={getValue<any>()} format="MMM D, YYYY" />
            </Text>
          );
        },
        meta: { filterOperator: "between" },
      },
      {
        id: "actions",
        header: "",
        size: 44,
        cell: function render({ row: { original: record } }) {
          return (
            <RowActions
              id={record.id}
              email={record.email}
              onSendEmail={handleSendEmail}
              onReschedule={handleOpenReschedule}
            />
          );
        },
        meta: { isPlaceholder: true },
      },
    ],
    [handleSendEmail, handleOpenReschedule],
  );

  const {
    getHeaderGroups,
    getRowModel,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { data: tableData, isFetching },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ],
      },
      pagination: {
        pageSize: 10,
      },
    },
  });

  const headers = getFlatHeaders();
  const emailColumn = headers.find((h) => h.id === "email")?.column;
  const sourceColumn = headers.find((h) => h.id === "source")?.column;
  const statusColumn = headers.find((h) => h.id === "status")?.column;
  const orderNumberColumn = headers.find(
    (h) => h.id === "order_number",
  )?.column;
  const shippingCountryColumn = headers.find(
    (h) => h.id === "shipping_country",
  )?.column;
  const sentAtHeader = headers.find((h) => h.id === "sent_at");
  const createdAtHeader = headers.find((h) => h.id === "created_at");

  const sourceValue = (sourceColumn?.getFilterValue() as string) ?? null;
  const statusValue = (statusColumn?.getFilterValue() as string) ?? null;

  const appliedFilters = headers.filter((h) => h.column.getIsFiltered());
  const hasActiveFilters = appliedFilters.length > 0;

  const clearAllFilters = () => {
    appliedFilters.forEach((h) => h.column.setFilterValue(undefined));
  };

  // Compute summary stats from loaded data
  const allRecords = tableData?.data ?? [];
  const totalCount = tableData?.total ?? 0;

  return (
    <List canCreate={false}>
      {/* ── Summary stats ───────────────────────────────────── */}
      <Flex gap="sm" mb="lg" wrap="wrap">
        <StatCard
          icon={<IconMail size={18} />}
          label="Total"
          value={totalCount}
          color="blue"
          isLoading={isFetching}
        />
        <StatCard
          icon={<IconCalendarEvent size={18} />}
          label="Scheduled"
          value={allRecords.filter((r) => r.status === "scheduled").length}
          color="blue"
          isLoading={isFetching}
        />
        <StatCard
          icon={<IconSend size={18} />}
          label="Sent"
          value={allRecords.filter((r) => r.status === "sent").length}
          color="green"
          isLoading={isFetching}
        />
        <StatCard
          icon={<IconAlertCircle size={18} />}
          label="Failed"
          value={allRecords.filter((r) => r.status === "failed").length}
          color="red"
          isLoading={isFetching}
        />
      </Flex>

      {/* ── Filter bar ──────────────────────────────────────── */}
      <Paper
        p="md"
        mb="md"
        radius="md"
        sx={(theme) => ({
          backgroundColor:
            theme.colorScheme === "dark"
              ? theme.colors.dark[7]
              : theme.colors.gray[0],
          border: `1px solid ${
            theme.colorScheme === "dark"
              ? theme.colors.dark[5]
              : theme.colors.gray[2]
          }`,
        })}
      >
        <Group position="apart" mb={filtersOpen ? "md" : 0}>
          <UnstyledButton onClick={() => setFiltersOpen((o) => !o)}>
            <Group spacing={8}>
              <ThemeIcon
                size={28}
                radius="md"
                variant="light"
                color={hasActiveFilters ? "blue" : "gray"}
              >
                <IconFilter size={14} />
              </ThemeIcon>
              <Text size="sm" weight={600}>
                Filters
              </Text>
              {hasActiveFilters && (
                <Badge
                  size="xs"
                  variant="filled"
                  color="blue"
                  radius="xl"
                  sx={{ minWidth: 20 }}
                >
                  {appliedFilters.length}
                </Badge>
              )}
              {filtersOpen ? (
                <IconChevronUp size={14} />
              ) : (
                <IconChevronDown size={14} />
              )}
            </Group>
          </UnstyledButton>

          {hasActiveFilters && (
            <Tooltip label="Clear all filters" withinPortal>
              <ActionIcon
                variant="light"
                color="gray"
                size="sm"
                radius="xl"
                onClick={clearAllFilters}
              >
                <IconFilterOff size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        <Collapse in={filtersOpen}>
          <Grid gutter="md">
            {emailColumn && (
              <Grid.Col span={4}>
                <DebouncedColumnInput
                  column={emailColumn}
                  label="Email"
                  placeholder="Search by email..."
                  size="xs"
                />
              </Grid.Col>
            )}
            <Grid.Col span={4}>
              <Select
                label="Source"
                placeholder="All sources"
                data={SOURCE_OPTIONS}
                value={sourceValue}
                onChange={(value) =>
                  sourceColumn?.setFilterValue(value || undefined)
                }
                clearable
                size="xs"
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Select
                label="Status"
                placeholder="All statuses"
                data={STATUS_OPTIONS}
                value={statusValue}
                onChange={(value) =>
                  statusColumn?.setFilterValue(value || undefined)
                }
                clearable
                size="xs"
              />
            </Grid.Col>
            {orderNumberColumn && (
              <Grid.Col span={4}>
                <DebouncedColumnInput
                  column={orderNumberColumn}
                  label="Order Number"
                  placeholder="Search order..."
                  size="xs"
                />
              </Grid.Col>
            )}
            {shippingCountryColumn && (
              <Grid.Col span={4}>
                <DebouncedColumnInput
                  column={shippingCountryColumn}
                  label="Shipping Country"
                  placeholder="Search country..."
                  size="xs"
                />
              </Grid.Col>
            )}
            {sentAtHeader && (
              <Grid.Col span={6}>
                <Text size="xs" weight={500} mb={4}>
                  Sent At
                </Text>
                <DateFilter
                  header={sentAtHeader}
                  defaultMode="single"
                  enableModeToggle={false}
                  size="xs"
                />
              </Grid.Col>
            )}
            {createdAtHeader && (
              <Grid.Col span={6}>
                <Text size="xs" weight={500} mb={4}>
                  Created At
                </Text>
                <DateFilter
                  header={createdAtHeader}
                  defaultMode="single"
                  enableModeToggle={false}
                  size="xs"
                />
              </Grid.Col>
            )}
          </Grid>
        </Collapse>

        {/* Applied filter chips */}
        {hasActiveFilters && !filtersOpen && (
          <Flex gap={6} wrap="wrap" mt="xs">
            {appliedFilters.map((header) => {
              const filterValue = header.column.getFilterValue();
              let displayValue: string;

              if (header.id === "source") {
                displayValue =
                  SOURCE_LABELS[filterValue as string] ||
                  (filterValue as string);
              } else if (header.id === "status") {
                displayValue =
                  STATUS_LABELS[filterValue as string] ||
                  (filterValue as string);
              } else if (
                header.id === "sent_at" ||
                header.id === "created_at"
              ) {
                const arr = filterValue as string[];
                displayValue = arr
                  ? `${arr[0]?.split("T")[0] ?? ""} — ${arr[1]?.split("T")[0] ?? ""}`
                  : String(filterValue);
              } else {
                displayValue = String(filterValue);
              }

              return (
                <Badge
                  key={header.id}
                  variant="outline"
                  color="blue"
                  size="sm"
                  radius="xl"
                  pr={3}
                  rightSection={
                    <ActionIcon
                      size={14}
                      color="pink"
                      radius="xl"
                      variant="transparent"
                      onClick={() => header.column.setFilterValue(undefined)}
                    >
                      <IconX size={10} />
                    </ActionIcon>
                  }
                >
                  {header.column.columnDef.header as string}: {displayValue}
                </Badge>
              );
            })}
          </Flex>
        )}
      </Paper>

      {/* ── Table ───────────────────────────────────────────── */}
      <Paper
        radius="md"
        sx={(theme) => ({
          border: `1px solid ${
            theme.colorScheme === "dark"
              ? theme.colors.dark[4]
              : theme.colors.gray[2]
          }`,
          overflow: "hidden",
        })}
      >
        <ScrollArea>
          <Box pos="relative" sx={{ minWidth: "100%" }}>
            <LoadingOverlay
              visible={isFetching}
              overlayOpacity={0.3}
              radius="md"
            />
            <Table
              highlightOnHover
              verticalSpacing={10}
              horizontalSpacing="md"
              sx={(theme) => ({
                tableLayout: "auto",
                width: "100%",
                "thead tr th": {
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[7]
                      : theme.colors.gray[0],
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase" as const,
                  letterSpacing: 0.6,
                  color:
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[1]
                      : theme.colors.gray[6],
                  borderBottom: `2px solid ${
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[4]
                      : theme.colors.gray[2]
                  }`,
                  padding: "10px 16px",
                },
                "tbody tr td": {
                  borderBottom: `1px solid ${
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[5]
                      : theme.colors.gray[1]
                  }`,
                  padding: "10px 16px",
                },
                "tbody tr:last-of-type td": {
                  borderBottom: "none",
                },
                "tbody tr:hover": {
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[6]
                      : theme.fn.lighten(theme.colors.pink[0], 0.5),
                },
              })}
            >
              <thead>
                {getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          whiteSpace: "nowrap",
                          cursor: header.column.getCanSort()
                            ? "pointer"
                            : "default",
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {!header.isPlaceholder &&
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        {header.column.getIsSorted() === "asc" && " ↑"}
                        {header.column.getIsSorted() === "desc" && " ↓"}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {getRowModel().rows.length === 0 && !isFetching && (
                  <tr>
                    <td colSpan={columns.length}>
                      <Flex direction="column" align="center" py={48} gap="xs">
                        <ThemeIcon
                          size={48}
                          radius="xl"
                          variant="light"
                          color="gray"
                        >
                          <IconMail size={24} />
                        </ThemeIcon>
                        <Text size="sm" color="dimmed" weight={500}>
                          No review requests found
                        </Text>
                        <Text size="xs" color="dimmed">
                          Try adjusting your filters or create a new request
                        </Text>
                      </Flex>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Box>
        </ScrollArea>
      </Paper>

      {/* ── Pagination ──────────────────────────────────────── */}
      <Flex justify="space-between" align="center" mt="md">
        <Text size="xs" color="dimmed">
          Page {current} of {pageCount || 1}
        </Text>
        <Pagination
          total={pageCount}
          page={current}
          onChange={setCurrent}
          radius="md"
          size="sm"
        />
      </Flex>
      {/* Reschedule Modal */}
      <Modal
        opened={rescheduleOpened}
        onClose={() => setRescheduleOpened(false)}
        title="Reschedule Review Request"
        centered
      >
        <Text size="sm" color="dimmed" mb="md">
          Pick a new date and time for this review request to be sent.
        </Text>
        <Flex gap="sm" mb="lg">
          <DatePicker
            label="Date"
            placeholder="Pick date"
            value={rescheduleDate}
            onChange={setRescheduleDate}
            withinPortal
            sx={{ flex: 1 }}
          />
          <TimeInput
            label="Time"
            value={rescheduleTime}
            onChange={setRescheduleTime}
            sx={{ flex: 1 }}
          />
        </Flex>
        <Group position="right">
          <Button variant="default" onClick={() => setRescheduleOpened(false)}>
            Cancel
          </Button>
          <Button
            color="blue"
            loading={isRescheduling}
            disabled={!rescheduleDate}
            onClick={handleReschedule}
          >
            Reschedule
          </Button>
        </Group>
      </Modal>
    </List>
  );
};
